import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, API } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LogOut, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [sub, st] = await Promise.all([
        api.get("/admin/submissions", { params: filter === "all" ? {} : { fit: filter } }),
        api.get("/admin/stats"),
      ]);
      setItems(sub.data.items);
      setStats(st.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("mf_admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("mf_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const logout = () => {
    localStorage.removeItem("mf_admin_token");
    localStorage.removeItem("mf_admin_user");
    navigate("/admin/login");
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("mf_admin_token");
      const params = filter === "all" ? "" : `?fit=${filter}`;
      const res = await fetch(`${API}/admin/submissions.csv${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : "mentorfit-submissions.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Could not download CSV");
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 mx-auto w-full max-w-7xl px-6 sm:px-10 py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="overline">Admin</p>
            <h1 className="font-serif text-5xl mt-4">Submissions</h1>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <button onClick={load} data-testid="admin-refresh-btn" className="overline flex items-center gap-2 hover:text-[hsl(var(--gold))] transition-colors">
              <RefreshCw size={14} strokeWidth={1.5} /> Refresh
            </button>
            <button onClick={downloadCsv} data-testid="admin-download-csv-btn" className="overline flex items-center gap-2 hover:text-[hsl(var(--gold))] transition-colors">
              <Download size={14} strokeWidth={1.5} /> Export CSV
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="overline flex items-center gap-2 hover:text-[hsl(var(--gold))] transition-colors">
              <LogOut size={14} strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "hsl(var(--taupe))" }} data-testid="admin-stats">
          <StatCard label="Total submissions" value={stats?.total ?? "—"} testid="stat-total" />
          <StatCard label="Great fit" value={stats?.great_fit ?? "—"} accent="hsl(var(--gold))" testid="stat-great-fit" />
          <StatCard label="Foundational classes" value={stats?.foundational_classes ?? "—"} accent="hsl(var(--sage))" testid="stat-foundational" />
        </div>

        {/* Top goals */}
        {stats?.top_goals?.length > 0 && (
          <section className="mt-16" data-testid="admin-top-goals">
            <p className="overline">Top goals selected</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {stats.top_goals.map((g) => (
                <div
                  key={g.tag}
                  className="flex items-center justify-between px-5 py-4"
                  style={{ border: "1px solid hsl(var(--taupe))", background: "hsl(var(--ivory))" }}
                  data-testid={`goal-row-${g.tag}`}
                >
                  <span className="text-[hsl(var(--plum))]">{g.label}</span>
                  <span className="font-serif text-2xl" style={{ color: "hsl(var(--gold))" }}>
                    {g.count}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Filter + table */}
        <section className="mt-20">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h2 className="font-serif text-3xl">All entries</h2>
            <div className="w-56" data-testid="admin-filter-wrapper">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="admin-filter-trigger" className="rounded-none border-[hsl(var(--taupe))]">
                  <SelectValue placeholder="Filter by fit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="filter-all">All entries</SelectItem>
                  <SelectItem value="great_fit" data-testid="filter-great-fit">Great fit</SelectItem>
                  <SelectItem value="foundational_classes" data-testid="filter-foundational">Foundational classes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div
            className="overflow-x-auto"
            style={{ border: "1px solid hsl(var(--taupe))", background: "hsl(var(--ivory))" }}
            data-testid="admin-submissions-table-wrapper"
          >
            <table className="w-full text-sm" data-testid="admin-submissions-table">
              <thead>
                <tr className="text-left overline" style={{ borderBottom: "1px solid hsl(var(--taupe))" }}>
                  <th className="px-6 py-4">Submitted</th>
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Email</th>
                  <th className="px-6 py-4">Score</th>
                  <th className="px-6 py-4">Result</th>
                  <th className="px-6 py-4">Top goals</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[hsl(var(--plum-soft))]" data-testid="admin-loading-row">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[hsl(var(--plum-soft))]" data-testid="admin-empty-row">
                      No submissions yet.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((s) => (
                    <tr
                      key={s.id}
                      style={{ borderTop: "1px solid hsl(var(--taupe))" }}
                      data-testid={`submission-row-${s.id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-[hsl(var(--plum-soft))]">{fmtDate(s.created_at)}</td>
                      <td className="px-6 py-4 font-medium">{s.name}</td>
                      <td className="px-6 py-4 text-[hsl(var(--plum-soft))]">{s.email}</td>
                      <td className="px-6 py-4 font-serif text-xl">{s.score}</td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-block px-3 py-1 text-xs uppercase tracking-[0.15em]"
                          style={{
                            border: `1px solid ${s.fit_result === "great_fit" ? "hsl(var(--gold))" : "hsl(var(--sage))"}`,
                            color: s.fit_result === "great_fit" ? "hsl(var(--gold))" : "hsl(var(--sage))",
                          }}
                        >
                          {s.fit_result === "great_fit" ? "Great fit" : "Foundational"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[hsl(var(--plum-soft))]">
                        {(s.goal_labels || []).slice(0, 2).join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function StatCard({ label, value, accent = "hsl(var(--plum))", testid }) {
  return (
    <div
      className="p-10 flex flex-col gap-4"
      style={{ background: "hsl(var(--ivory))" }}
      data-testid={testid}
    >
      <p className="overline">{label}</p>
      <p className="font-serif text-6xl leading-none" style={{ color: accent }}>
        {value}
      </p>
    </div>
  );
}
